import PropTypes from 'prop-types';
import React, { memo, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { Form, useFormikContext } from 'formik';

import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import RemoveButton from 'core/components/remove-button';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import {
  ENTITY_TYPE_OPTIONS,
  FO41_DESCRIPTION_LABEL,
  FO41_DESCRIPTION_PLACEHOLDER,
  getDescriptionLabel,
  getDescriptionPlaceholder,
  getFilteredRelationshipOptions,
  getVisibilityFlags,
  isFO41ToggleVisible,
  isPartyEquipmentVisible
} from 'common/helpers/farm-interested-party-helpers';
import useStyles from '../../people/person.styles';

function FarmInterestedParty({
  locationOptions,
  equipmentOptions,
  riskOptions,
  sppOptions,
  rpsOptions,
  index,
  fieldName,
  item,
  removeFromList,
  onRemove,
  initialFold,
  onFold,
  disabled,
  includeFarmLiability,
  liabilityType,
  existingParties,
  hiddenRelationships,
  hideFO41,
  hideLossPayeeEquipment
}) {
  const classes = useStyles();
  const { setFieldValue } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  const context = inPolicyRoute ? 'policy' : 'offer';
  const emptyMsg = (itemType) =>
    `There are no ${itemType} on this ${context}. Please add one if you want to associate it to the interested party.`;
  const relationshipOptions = getFilteredRelationshipOptions({
    allOptions: lookupsJson.farm.farmInterestedPartyTypes,
    includeFarmLiability,
    liabilityType,
    existingParties,
    editingPartyId: item.id,
    // Keep mask-hidden relationships (e.g. GL-4002 on rentals, #32043) out of the dropdown when
    // editing an existing party too, not only when adding.
    hiddenRelationships
  });
  const isBillToOther = item.relationship === 'billToOther';
  // FO-41 add-on toggle: shown for eligible relationships unless the policy-type mask removes
  // FO-41 entirely (e.g. Rental, #32359).
  const showFO41Toggle = isFO41ToggleVisible({ relationship: item.relationship, hideFO41 });

  // Clear FO-41 fields when switching to a non-eligible relationship
  useEffect(() => {
    if (!showFO41Toggle && item.includeFO41) {
      setFieldValue(`${fieldName}.includeFO41`, false);
    }
  }, [item.relationship]);
  const {
    showLocationFields,
    showEquipmentFields,
    showLocationAndRiskFields,
    showLocationRiskAndEquipmentFields,
    showDescriptionOnly,
    showAllFields,
    showResidentInfo
  } = getVisibilityFlags(item.relationship);
  // Loss Payee drops the equipment field on Home/Rental (#32521/#32525); keeps it on Farm (#32520).
  const showEquipmentField = isPartyEquipmentVisible({ relationship: item.relationship, hideLossPayeeEquipment });

  return (
    <CollapsibleCard
      content={
        <Label
          type="infoSubtitle"
          style={{
            display: 'inline',
            marginRight: 8
          }}
        >{`${item.name}`}</Label>
      }
      contentRight={!item.isPrimary && <RemoveButton mode="big" onClick={() => onRemove(index, removeFromList)} />}
      initialOpen={initialFold}
      onChange={(val) => onFold(index, val)}
    >
      <Form disabled={disabled} className={classes.container}>
        <Grid key="container" container justifyContent="space-around" alignItems="center" spacing={4}>
          <FormField
            mode="light"
            name={`${fieldName}.name`}
            id={`${fieldName}.name`}
            label="Name or Company"
            type="string"
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.address`}
            id={`${fieldName}.address.address`}
            label="Address"
            type="address"
            xs={8}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.address2`}
            id={`${fieldName}.address.address2`}
            label="Address 2"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.city`}
            id={`${fieldName}.address.city`}
            label="City"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.state`}
            id={`${fieldName}.address.state`}
            label="State"
            type="state"
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.zip`}
            id={`${fieldName}.address.zip`}
            label="Zip code"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.relationship`}
            id={`${fieldName}.relationship`}
            label="Relationship"
            type="select"
            options={relationshipOptions}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
          {isBillToOther && (
            <FormField
              mode="light"
              name={`${fieldName}.email`}
              id={`${fieldName}.email`}
              label="Email"
              type="string"
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {item.relationship === 'partnerOrCoOwner' && (
            <FormField
              mode="light"
              name={`${fieldName}.entityType`}
              id={`${fieldName}.entityType`}
              label="Entity Type"
              type="select"
              options={ENTITY_TYPE_OPTIONS}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {item.relationship === 'mortgageeCoOwner' && (
            <FormField
              mode="light"
              name={`${fieldName}.entityType`}
              id={`${fieldName}.entityType`}
              label="Entity type (form of interest)"
              type="select"
              options={lookupsJson.farm.gl108EntityTypeOptions}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {showLocationFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Locations"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                placeholder={getDescriptionPlaceholder(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showResidentInfo && (
            <>
              <NotificationCard className={classes.residentInfoBanner} iconSize="icon16" type="light" noShadow>
                This additional interest/insured is a resident of the household.
              </NotificationCard>
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showEquipmentFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.equipment`}
                id={`${fieldName}.equipment`}
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showLocationAndRiskFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showLocationRiskAndEquipmentFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.equipment`}
                id={`${fieldName}.equipment`}
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showDescriptionOnly && (
            <FormField
              mode="light"
              name={`${fieldName}.description`}
              id={`${fieldName}.description`}
              label={getDescriptionLabel(item.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {showAllFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              {showEquipmentField && (
                <FormField
                  mode="light"
                  name={`${fieldName}.equipment`}
                  id={`${fieldName}.equipment`}
                  label="Associated equipment"
                  description="Select all that apply"
                  type="chipArray"
                  options={equipmentOptions}
                  emptyMessage={emptyMsg('equipment')}
                  xs={12}
                  permissions={{ isLicensedAction: false }}
                />
              )}
              <FormField
                mode="light"
                name={`${fieldName}.sppItems`}
                id={`${fieldName}.sppItems`}
                label="Associated inland marine assets"
                description="Select all that apply"
                type="chipArray"
                options={sppOptions}
                emptyMessage={emptyMsg('inland marine assets')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.rpsItems`}
                id={`${fieldName}.rpsItems`}
                label="Associated related private structures"
                description="Select all that apply"
                type="chipArray"
                options={rpsOptions}
                emptyMessage={emptyMsg('related private structures')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showFO41Toggle && (
            <Grid container spacing={4} className={classes.fo41Container}>
              <FormField
                mode="light"
                name={`${fieldName}.includeFO41`}
                id={`${fieldName}.includeFO41`}
                label="Additional insureds - Property coverages, FO-41"
                type="switch"
                tooltipLabel="More Info"
                tooltipText="Turn this on when the additional insured has full or partial interest in physical property on the farm. This may include a dwelling, barn, machinery, or other equipment. Once added, select the location and describe the specific property of interest."
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              {item.includeFO41 && (
                <>
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41Locations`}
                    id={`${fieldName}.fo41Locations`}
                    label="Associated locations/properties"
                    description="Select all that apply"
                    type="chipArray"
                    options={locationOptions}
                    emptyMessage={emptyMsg('locations')}
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41Risks`}
                    id={`${fieldName}.fo41Risks`}
                    label="Associated risks"
                    description="Select all that apply"
                    type="chipArray"
                    options={riskOptions}
                    emptyMessage={emptyMsg('risks')}
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41Equipment`}
                    id={`${fieldName}.fo41Equipment`}
                    label="Associated equipment"
                    description="Select all that apply"
                    type="chipArray"
                    options={equipmentOptions}
                    emptyMessage={emptyMsg('equipment')}
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41SppItems`}
                    id={`${fieldName}.fo41SppItems`}
                    label="Associated inland marine assets"
                    description="Select all that apply"
                    type="chipArray"
                    options={sppOptions}
                    emptyMessage={emptyMsg('inland marine assets')}
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41RpsItems`}
                    id={`${fieldName}.fo41RpsItems`}
                    label="Associated related private structures"
                    description="Select all that apply"
                    type="chipArray"
                    options={rpsOptions}
                    emptyMessage={emptyMsg('related private structures')}
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name={`${fieldName}.fo41Description`}
                    id={`${fieldName}.fo41Description`}
                    label={FO41_DESCRIPTION_LABEL}
                    placeholder={FO41_DESCRIPTION_PLACEHOLDER}
                    type="textarea"
                    minRows={3}
                    maxRows={3}
                    xs={12}
                    className={classes.fo41Description}
                    permissions={{ isLicensedAction: false }}
                  />
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Form>
    </CollapsibleCard>
  );
}

FarmInterestedParty.propTypes = {
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array,
  index: PropTypes.number.isRequired,
  fieldId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    isPrimary: PropTypes.bool,
    name: PropTypes.string.isRequired,
    relationship: PropTypes.string.isRequired,
    address: PropTypes.object
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  removeFromList: PropTypes.func.isRequired,
  initialFold: PropTypes.bool.isRequired,
  onFold: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  includeFarmLiability: PropTypes.bool,
  liabilityType: PropTypes.string,
  existingParties: PropTypes.array,
  hiddenRelationships: PropTypes.arrayOf(PropTypes.string),
  hideFO41: PropTypes.bool,
  hideLossPayeeEquipment: PropTypes.bool
};

FarmInterestedParty.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: [],
  includeFarmLiability: false,
  liabilityType: undefined,
  existingParties: [],
  hiddenRelationships: [],
  hideFO41: false,
  hideLossPayeeEquipment: false
};

export default memo(FarmInterestedParty);
